import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from "@sentry/react"
import { notifications } from '@mantine/notifications'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [authError, setAuthError] = useState(null)

  // NEW: Add impersonation state
  const [impersonatedUser, setImpersonatedUser] = useState(null)
  const [realAdminUser, setRealAdminUser] = useState(null)

  // NEW: Effective user is impersonated user if active, otherwise real user
  const effectiveUser = impersonatedUser || user

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session?.user) {
        fetchUserProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session?.user) {
        // Check if user needs to set password - but avoid infinite loops
        const currentPath = window.location.pathname
        const isOnSetupPage = currentPath.includes('/setup-password')
        const isOnClearPage = currentPath.includes('/clear-temp-password')
        const needsPasswordSetup = session.user.user_metadata?.temp_password === true
        
        if (needsPasswordSetup && !isOnSetupPage && !isOnClearPage) {
          navigate('/setup-password')
        } else {
          fetchUserProfile(session.user.id)
        }
      } else {
        setUser(null)
        localStorage.removeItem('user')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchUserProfile = async (authId) => {
    try {
      setAuthError(null)
      // Simple query - just get the user
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', authId)
        .single()

      if (error) throw error

      if (userData) {
        // Get organization separately
        let orgName = null
        if (userData.organization_id) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', userData.organization_id)
            .single()
          
          if (orgData) {
            orgName = orgData.name
          }
        }

        const userProfile = {
          id: userData.id,
          authId: userData.auth_id,
          email: userData.email,
          name: userData.name || userData.email.split('@')[0],
          avatar: userData.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || userData.email)}&background=${userData.role === 'admin' ? '8B5CF6' : '3B82F6'}&color=fff`,
          role: userData.role,
          organization_id: userData.organization_id,  // Use snake_case to match database
          organizationId: userData.organization_id,    // Keep camelCase for backward compatibility
          organizationName: orgName || 'Tech Corp',
          department: userData.department,
          created_at: userData.created_at,
          last_active: userData.last_active
        }
        
        setUser(userProfile)
        localStorage.setItem('user', JSON.stringify(userProfile))
        // Also store organizationId separately for easy access
        if (userData.organization_id) {
          localStorage.setItem('organizationId', userData.organization_id)
        }

        // Set Sentry user context for error tracking
        if (import.meta.env.VITE_ENABLE_SENTRY === 'true') {
          Sentry.setUser({
            id: userProfile.id,
            email: userProfile.email,
            username: userProfile.email,
            role: userProfile.role,
            organization_id: userProfile.organization_id,
            organization_name: orgName
          })
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching user profile:', error)
      setAuthError('Unable to load your profile. Please try refreshing the page.')
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error
      
      return { success: true }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign in error:', error)
      return { 
        success: false, 
        error: error.message || 'Invalid credentials'
      }
    }
  }

  const signUp = async (name, email, password, organizationId = null) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
            organization_id: organizationId
          }
        }
      })

      if (error) throw error
      return { success: true }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign up error:', error)
      return { 
        success: false, 
        error: error.message 
      }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign out error:', error)
    } finally {
      // Always clear local state, even if the API call failed
      if (import.meta.env.VITE_ENABLE_SENTRY === 'true') {
        Sentry.setUser(null)
      }
      setUser(null)
      setAuthError(null)
      localStorage.removeItem('user')
      navigate('/signin')
    }
  }

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/setup-password`
      })

      if (error) throw error

      return { 
        success: true, 
        message: 'Password reset email sent' 
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Password reset error:', error)
      return { 
        success: false, 
        error: error.message 
      }
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error
      return { success: true }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Password update error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // NEW: Impersonation methods with server-side audit logging
  const impersonateUser = async (learnerData) => {
    // Only admins can impersonate
    if (user?.role !== 'admin') {
      if (import.meta.env.DEV) console.error('Only admins can impersonate users')
      return
    }

    if (import.meta.env.DEV) console.log(`Admin ${user.email} impersonating learner ${learnerData.email}`)

    // SECURITY: Log impersonation start server-side
    try {
      const { data: auditResult, error: auditError } = await supabase.rpc(
        'log_impersonation_start',
        { p_target_user_id: learnerData.id }
      )

      if (auditError) {
        if (import.meta.env.DEV) console.error('Failed to log impersonation start:', auditError)
        // Don't block impersonation, but log the failure
      } else if (!auditResult?.success) {
        // Server-side validation failed (e.g., cross-org impersonation attempt)
        if (import.meta.env.DEV) console.error('Impersonation denied:', auditResult?.error)
        notifications.show({
          title: 'Impersonation Denied',
          message: auditResult?.error || 'Unable to impersonate this user',
          color: 'red',
          autoClose: 5000
        })
        return
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error logging impersonation:', error)
    }

    // Store real admin user
    setRealAdminUser(user)

    // Create impersonated user object (same format as regular user)
    const impersonated = {
      id: learnerData.id,
      authId: learnerData.auth_id,
      email: learnerData.email,
      name: learnerData.name || learnerData.email.split('@')[0],
      avatar: learnerData.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(learnerData.name || learnerData.email)}&background=3B82F6&color=fff`,
      role: learnerData.role,
      organization_id: learnerData.organization_id,
      organizationId: learnerData.organization_id,
      department: learnerData.department,
      created_at: learnerData.created_at,
      last_active: learnerData.last_active,
      // Flag to indicate this is impersonated (for read-only enforcement)
      _impersonated: true,
      _realAdmin: user.email
    }

    setImpersonatedUser(impersonated)

    // Navigate to learner dashboard
    navigate('/dashboard')

    notifications.show({
      title: 'Viewing as Learner',
      message: `Now viewing as: ${learnerData.name || learnerData.email}`,
      color: 'blue',
      autoClose: 3000
    })
  }

  const exitImpersonation = async () => {
    if (!impersonatedUser) return

    if (import.meta.env.DEV) console.log(`Exiting impersonation, restoring admin ${realAdminUser.email}`)

    // SECURITY: Log impersonation end server-side
    try {
      await supabase.rpc('log_impersonation_end', {
        p_target_user_id: impersonatedUser.id
      })
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error logging impersonation end:', error)
      // Don't block exit, just log the failure
    }

    setImpersonatedUser(null)
    setRealAdminUser(null)

    // Navigate back to admin area
    navigate('/admin/learners')

    notifications.show({
      title: 'Impersonation Ended',
      message: 'Returned to admin view',
      color: 'green',
      autoClose: 2000
    })
  }

  const value = {
    user: effectiveUser,  // Changed from 'user' to 'effectiveUser'
    session,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    // NEW: Add impersonation methods
    impersonateUser,
    exitImpersonation,
    isImpersonating: !!impersonatedUser,
    realAdminUser,
    isAuthenticated: !!session
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}